"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseRowShardOrderMismatchError = void 0;
exports.yValueToPlain = yValueToPlain;
exports.createDatabaseRowCellProjector = createDatabaseRowCellProjector;
exports.readRowShardRows = readRowShardRows;
exports.storedRowShardReadKeys = storedRowShardReadKeys;
exports.readStoredRowShardRows = readStoredRowShardRows;
const Y = __importStar(require("yjs"));
const database_column_lineage_1 = require("./database-column-lineage");
const database_row_shard_planning_1 = require("./database-row-shard-planning");
const database_title_cell_1 = require("./database-title-cell");
const database_document_scheme_1 = require("./generated/database-document-scheme");
const index_1 = require("./index");
/** A Yjs value as plain data: text as string, maps and arrays as JSON. */
function yValueToPlain(value) {
    if (value instanceof Y.Text)
        return value.toString();
    if (value instanceof Y.Array)
        return value.toArray().map(yValueToPlain);
    if (value instanceof Y.Map)
        return value.toJSON();
    if (value &&
        typeof value === 'object' &&
        'toJSON' in value &&
        typeof value.toJSON === 'function') {
        return value.toJSON();
    }
    return value;
}
/**
 * Puts the stored cells of one row under their column ids, as every reader
 * reads them: a column whose type changed reads its cells through its
 * lineage, a column stored under an earlier key reads it under its id, and
 * the cells of no column (a removed column leaves them) fall away. Split
 * roots persist ordinary columns only; the title is structural. Takes and
 * keeps `{ columnId, value }` cells, in place.
 */
function createDatabaseRowCellProjector(storedColumns) {
    const columns = storedColumns;
    const lineageColumns = (0, database_column_lineage_1.databaseLineageColumns)(storedColumns);
    const definedCellIds = new Set([
        'title',
        ...index_1.DATABASE_ROW_SYSTEM_FIELD_IDS,
        ...columns.map(column => column.id),
    ]);
    return cells => {
        if (lineageColumns.length)
            (0, database_column_lineage_1.projectDatabaseRowCells)(lineageColumns, cells, database_column_lineage_1.storedDatabaseCellValue, (columnId, value) => ({ columnId, value }));
        for (const column of columns) {
            const key = (0, database_title_cell_1.resolveDatabaseCellStorageKey)(cells, column.id, columns);
            if (key === column.id)
                continue;
            const legacy = cells[key];
            cells[column.id] =
                legacy && typeof legacy === 'object' && 'value' in legacy
                    ? { ...legacy, columnId: column.id }
                    : { columnId: column.id, value: legacy };
        }
        for (const key of Object.keys(cells))
            if (!definedCellIds.has(key))
                delete cells[key];
        return cells;
    };
}
/**
 * The rows of a decoded row shard in their order, each cell under its column
 * id. `expectedRowCount` is the count of the shard's directory entry, and the
 * order must name exactly the stored rows. Null reads the shard as it is
 * stored, for the index of its confirmed state: the rows the order names that
 * hold a row map, as editors may leave them meanwhile.
 */
function readRowShardRows(shardDoc, rowShardDocId, storedColumns, expectedRowCount, rowIds, columnIds) {
    const rows = shardDoc.getMap(database_document_scheme_1.DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    return readStoredRowShardRows({
        order: shardDoc.getArray(database_document_scheme_1.DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).toArray(),
        size: rows.size,
        has: rowId => rows.has(rowId),
        plain: rowId => yValueToPlain(rows.get(rowId)),
    }, rowShardDocId, storedColumns, expectedRowCount, rowIds, columnIds);
}
/**
 * The stored cell keys readStoredRowShardRows reads of a row to answer
 * `columnIds`: each column's id, the title a title column may sit under and
 * the keys of every lineage column, since the projection of one lineage
 * column writes under its id, which another column's lineage may read. A
 * reader that hands over only these cells of a row gets the same answer.
 */
function storedRowShardReadKeys(storedColumns, columnIds) {
    const keys = new Set(['title', ...columnIds]);
    for (const column of (0, database_column_lineage_1.databaseLineageColumns)(storedColumns))
        for (const key of (0, database_column_lineage_1.databaseColumnCellKeys)(column))
            keys.add(key);
    return [...keys];
}
/**
 * The rows of a shard disagree with the row count of its manifest entry or
 * with its own order: the shard was read at another version than the
 * manifest, or it is broken.
 */
class DatabaseRowShardOrderMismatchError extends Error {
    constructor(rowShardDocId, detail) {
        super(`Database row shard ${rowShardDocId} has invalid row order (${detail})`);
        this.rowShardDocId = rowShardDocId;
        this.detail = detail;
        this.name = 'DatabaseRowShardOrderMismatchError';
    }
}
exports.DatabaseRowShardOrderMismatchError = DatabaseRowShardOrderMismatchError;
/** readRowShardRows over rows a reader already took from the shard. */
function readStoredRowShardRows(rows, rowShardDocId, storedColumns, expectedRowCount, rowIds, columnIds) {
    const project = createDatabaseRowCellProjector(storedColumns);
    const requested = rowIds ? new Set(rowIds) : null;
    const requestedColumns = columnIds ? new Set(columnIds) : null;
    const rowsJson = {};
    const orderedRowIds = (0, database_row_shard_planning_1.canonicalDatabaseRowOrder)(rows.order.map(rowId => String(rowId)));
    if (rows.size > 0 && orderedRowIds.length === 0) {
        throw new Error(`Database row shard ${rowShardDocId} is missing ${database_document_scheme_1.DATABASE_ROW_SHARD_ORDER_ARRAY_KEY}`);
    }
    const asStored = expectedRowCount === null;
    if (!asStored &&
        (rows.size !== expectedRowCount ||
            orderedRowIds.length !== rows.size ||
            orderedRowIds.some(id => !rows.has(id)))) {
        throw new DatabaseRowShardOrderMismatchError(rowShardDocId, `rows ${rows.size}, manifest ${expectedRowCount}, order ${orderedRowIds.length}`);
    }
    for (const rowId of orderedRowIds) {
        if (requested && !requested.has(rowId))
            continue;
        const plain = rows.plain(rowId);
        if (!plain || typeof plain !== 'object' || Array.isArray(plain)) {
            if (asStored)
                continue;
            throw new Error(`Database row shard ${rowShardDocId} has an invalid row snapshot`);
        }
        project(plain);
        if (requestedColumns &&
            plain &&
            typeof plain === 'object' &&
            !Array.isArray(plain)) {
            rowsJson[rowId] = Object.fromEntries(Object.entries(plain).filter(([columnId]) => requestedColumns.has(columnId)));
        }
        else {
            rowsJson[rowId] = plain;
        }
    }
    return rowsJson;
}
